import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import { Button, Divider } from "@mui/material";
import { UserContext } from "../app/App";
import UploadDialog from "./UploadDialog";
import ConfigData from "../config/config.json";

const webserver = ConfigData.WEBSERVER;
//const label = { inputProps: { 'aria-label': 'Switch demo' } };

const collator = new Intl.Collator("de", { sensitivity: "base" });

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (orderBy === "name") {
    //return a[orderBy].localeCompare(b[orderBy], 'de', {sensitivity: 'base'})
    return collator.compare(a[orderBy], b[orderBy]);
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => -descendingComparator(a, b, orderBy)
    : (a, b) => descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Apotheke",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: "0px" }}></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "center" : "center"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function PharmaciesListExaminer(props) {
  const [openID, setOpenID] = React.useState(-1);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const [rowss, setRowss] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/examProcess/get/" +
        props.selectedProcess,
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].descriptionExpanded = false;
          data[i].titleExpanded = false;
          data[i].deleteOpen = false;
        }
        setRowss(data);
      })
      .catch((error) => {});
  }, [props.selectedProcess]);

  const filteredRows = rowss.filter((row) => {
    return (
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.id.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowss.length) : 0;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        paddingTop: 2,
      }}
    >
      <Box sx={{ width: "66%", alignSelf: "center" }}>
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={"medium"}>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                setRowss={setRowss}
                rowss={rowss}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(filteredRows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, pageIndex) => {
                    let index = 0;

                    for (let found = 0; found < rowss.length; found++) {
                      if (rowss[found].id === row.id) {
                        index = found;
                        break;
                      }
                    }
                    //const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <Row
                        key={row.id}
                        keyProp={row.id}
                        row={row}
                        openID={openID}
                        setOpenID={setOpenID}
                        setRowss={setRowss}
                        index={index}
                        selectedProcess={props.selectedProcess}
                        setRows={props.setRows}
                      />
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 73 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rowss.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => {
              return "" + from + "-" + to + " von " + count;
            }}
            labelRowsPerPage="Zeilen pro Seite"
          />
        </Paper>
      </Box>
    </Box>
  );
}

function Row(props) {
  const { row } = props;
  const { user } = React.useContext(UserContext);

  function setInProgress(id, value, key, setOpenID) {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/pharmacy/setInProgress/" +
        id +
        "?value=" +
        value +
        "&examiner=" +
        user.name,
      { method: "PUT", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        if (data === true) {
          setOpenID(key);
        } else {
          alert(
            "Diese Apotheke wird gerade von einem anderen Prüfer angerufen!"
          );
        }
      })
      .catch((error) => {});
  }

  return (
    <React.Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
        hover
        onClick={() => {
          if (props.openID === props.keyProp) {
            setInProgress(props.keyProp, "false", -1, props.setOpenID);
          } else {
            if (props.openID !== -1) {
              setInProgress(
                props.openID,
                "false",
                props.keyProp,
                props.setOpenID
              );
            }
            setInProgress(
              props.keyProp,
              "true",
              props.keyProp,
              props.setOpenID
            );
          }
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              if (props.openID === props.keyProp) {
                setInProgress(props.keyProp, "false", -1, props.setOpenID);
              } else {
                if (props.openID !== -1) {
                  setInProgress(
                    props.openID,
                    "false",
                    props.keyProp,
                    props.setOpenID
                  );
                }
                setInProgress(
                  props.keyProp,
                  "true",
                  props.keyProp,
                  props.setOpenID
                );
              }
            }}
          >
            {props.keyProp === props.openID ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" align="center">
          {row.name}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse
            in={props.keyProp === props.openID}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{ margin: 2 }}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Adresse</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell align="right">Dokumentationsbogen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={0}>
                    <TableCell component="th" scope="row">
                      {row.streetAddress}
                      <br />
                      {row.postalCode + " " + row.city}
                    </TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() =>
                          props.setRowss((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[props.index].uploadOpen = true;
                            return temp;
                          })
                        }
                      >
                        Einreichen
                      </Button>
                    </TableCell>
                    <UploadDialog
                      function={() => {
                        props.setRowss((prev) => {
                          let rowsCopy = JSON.parse(JSON.stringify(prev));
                          rowsCopy.splice(props.index, 1);
                          return rowsCopy;
                        });
                        props.setRows((prev) => {
                          let rowsCopy = JSON.parse(JSON.stringify(prev));
                          for (var i = 0; i < rowsCopy.length; i++) {
                            if (rowsCopy[i].id === props.selectedProcess) {
                              rowsCopy[i].results = rowsCopy[i].results.concat([
                                {},
                              ]);
                            }
                          }

                          return rowsCopy;
                        });
                      }}
                      open={row.uploadOpen}
                      setOpen={() =>
                        props.setRowss((prev) => {
                          let temp = JSON.parse(JSON.stringify(prev));
                          temp[props.index].uploadOpen = false;
                          return temp;
                        })
                      }
                      apoID={row.id}
                      pharmacyName={row.name}
                      selectedProcess={props.selectedProcess}
                    />
                  </TableRow>
                </TableBody>
              </Table>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: 2,
                }}
              >
                <Typography variant="subtitle2">Szenario</Typography>
                <Typography variant="body2">{row.scenario}</Typography>
              </Box>

              <Divider sx={{ m: 2 }}></Divider>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: 2,
                }}
              >
                <Typography variant="subtitle2">Rezeptur</Typography>
                <Typography variant="body2">{row.recipe}</Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}
